import React, {useEffect} from "react";
import './editor-brands-container.scss';
import {useDispatch, useSelector} from "react-redux";
import {BrandsHeaderDropdown} from "./components/brands-header-dropdown";
import {getBrandsAndActiveBrandsForUser} from "../mystuff-brands/brands-thunk";
import {executeThunk} from "@Utils/thunk.util";
import {
    areThereNoBrandsToShow, BRANDS_LOADING_STATES, canUserAccessBrandKits,
    doesUserHaveMultipleBrands,
    doesUserHaveSingleBrand,
    fetchBrandsResourceIfNotLoaded,
    getActiveBrandName,
} from "@Libraries/brands-library";
import PropTypes from "prop-types";
import {setBrandsLoadingState} from "../mystuff-brands/brands-reducer";
import {useActiveBrandId} from "@Hooks/brands/useActiveBrandId";
import Emitter from '@/services/event-emitter';
import {Icon} from "@Components/icon-v2";
import {IconSize} from "@Components/icon-v2/icon.types";

/**
 * The main component displaying the brands in Editor
 * @author Afifa Saleem <afifa@250mils.com>
 */
const EditorBrandsContainer = ({brandsClass, brandsHeaderClass, brandsHeaderTextClass, children}) => {
    const brands = useSelector(({brands}) => brands.brands),
        activeBrandId = useActiveBrandId(),
        brandsLoadingState = useSelector(({brands}) => brands.brandsLoadingState),
        dispatchReducer = useDispatch();

    const fetchBrands = () => {
        if (canUserAccessBrandKits()) {
            executeThunk(() => dispatchReducer(getBrandsAndActiveBrandsForUser()));
        } else {
            dispatchReducer(setBrandsLoadingState(BRANDS_LOADING_STATES.LOADED));
        }
    }

    useEffect(() => {
        Emitter.on('user:loggedIn', onUserLoggedIn);

        return () => {
            Emitter.off('user:loggedIn', onUserLoggedIn);
        };
    }, []);

    useEffect(() => {
        fetchBrandsResourceIfNotLoaded(brandsLoadingState, fetchBrands);
    }, []);

    const onUserLoggedIn = async () => {
        await fetchBrandsResourceIfNotLoaded(BRANDS_LOADING_STATES.NOT_LOADED, fetchBrands);
    }

    const getNoDropdownHeader = () => {
        return (
            <div className={'brand-heading ' + brandsHeaderClass + (doesUserHaveSingleBrand(brands) || areThereNoBrandsToShow(brands, brandsLoadingState) ? '' : ' _hidden')}>
                <div className={'title-with-brand-icon flex-row-align-center size-icon-20'}>
                    <i className={'icon icon-brands -brands-icon content-body _unmargin'}/>
                    <p className={'heading-title ' + brandsHeaderTextClass}>
                        {doesUserHaveSingleBrand(brands) ? getActiveBrandName(brands, activeBrandId) : i18next.t('pmwjs_brand_kits')}
                    </p>
                </div>
                {!canUserAccessBrandKits() ? <Icon className={'icon -icon-premium spacing-m-b-1'} icon={'icon-crown'} size={IconSize.SIZE_ICON_20}/> : ''}
            </div>
        );
    }

    return (
        <div className={'brand-container ' + brandsClass}>
            {getNoDropdownHeader()}
            {doesUserHaveMultipleBrands(brands) ? <BrandsHeaderDropdown
                dropdownLabelClass={brandsHeaderTextClass}
            /> : ''}
            {children}
        </div>
    )
}


EditorBrandsContainer.propTypes = {
    /**
     * optional string that is used to define your own custom classes to the header of editor brand container
     */
    brandsHeaderClass: PropTypes.string,
    /**
     * optional string that is used to define your own custom classes to the header text of editor brand container
     */
    brandsHeaderTextClass: PropTypes.string,
    /**
     * optional string that is used to define your own custom classes to the root of editor brand container
     */
    brandsClass: PropTypes.string
};

EditorBrandsContainer.defaultProps = {
    brandsClass: '',
    brandsHeaderClass: '',
    brandsHeaderTextClass: ''
};

export default EditorBrandsContainer;
